import { useState, useEffect, useRef, Fragment } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { cloneDeep } from "lodash";
import { useTranslation } from "react-i18next";
import { AxiosError, AxiosResponse } from "axios";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { TextInput } from "../../components/TextInput/TextInput";
import { DateInput } from "../../components/DateInput/DateInput";
import { CheckboxInput } from "../../components/CheckboxInput/CheckboxInput";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { PdfPreview } from "../../components/PdfPreview/PDFPreview";
import { Warehouse } from "../../models/Warehouse";
import { Product } from "../../models/Product";
import { OperatorOrderDetails } from "../../models/OperatorOrder";
import { ProcessingRequestDetails } from "../../models/ProcessingRequest";
import {
  AtestType,
  ProcessingOrderDetails,
} from "../../models/ProcessingOrder";
import { Utility } from "../../core/Utility";
import { OperatorOrderForm } from "../../forms/OperatorOrderForm/OperatorOrderForm";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllProducts } from "../../services/ProductService";
import {
  getOneProcessingOrder,
  addNewProcessingOrder,
  updateOneProcessingOrder,
  getProcessingRequests,
  getOperatorOrders,
  getProcessingOrders,
  getOneOperatorOrder,
} from "../../services/FinishingCenterService";
import { getProcessingOrderTemplate } from "../../document-templates/ProcessingOrderTemplate";
import "./ProcessingOrderOverviewPage.scss";

export interface ProcessingOverviewPageProps {
  show?: string;
  buttonSaveStyle?: React.CSSProperties;
  buttonNewPartnerStyle?: React.CSSProperties;
  buttonResetStyle?: React.CSSProperties;
}

export const ProcessingOrderOverviewPage = (p: ProcessingOverviewPageProps) => {
  const { buttonSaveStyle, buttonNewPartnerStyle, buttonResetStyle } = p;

  const { id } = useParams();
  const navigate = useNavigate();

  const { t } = useTranslation();

  const [updating, setUpdating] = useState<boolean>(false);
  const [warehousesApi, setWarehousesApi] = useState<Warehouse[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [newProcessingOrder, setNewProcessingOrder] =
    useState<ProcessingOrderDetails>({} as ProcessingOrderDetails);
  const [processingOrders, setProcessingOrders] = useState<
    ProcessingOrderDetails[]
  >([]);
  const [availableAmountKg, setAvailableAmountKg] = useState<
    number | undefined
  >(undefined);
  const [requests, setRequests] = useState<ProcessingRequestDetails[]>([]);
  const [operatorOrder, setOperatorOrder] = useState<
    OperatorOrderDetails | undefined
  >(undefined);
  const [isPdfReady, setIsPdfReady] = useState(false);
  const [processing, setProcessing] = useState<boolean>(false);

  const [pdfData, setPdfData] = useState<any>(
    getProcessingOrderTemplate(newProcessingOrder),
  );

  const downloadPdfDocument = useRef(null);
  const printPdfDocument = useRef(null);

  const calculateAvailableAmountForRequest = (
    requestId: string,
    requestAmountKg?: number,
  ) => {
    const totalRequestAmount = Number(requestAmountKg ?? 0);

    const usedAmount = processingOrders.reduce((sum: number, order: any) => {
      const orderRequestId = order.requestId ?? order.request?.id;

      if (orderRequestId !== requestId) {
        return sum;
      }

      if (newProcessingOrder.id && order.id === newProcessingOrder.id) {
        return sum;
      }

      return sum + Number(order.totalAmountKg ?? 0);
    }, 0);

    const available = totalRequestAmount - usedAmount;
    return available > 0 ? available : 0;
  };

  const recalculatePackageCount = (
    totalAmountKg?: number,
    packingType?: string,
  ): number | undefined => {
    const total = Number(totalAmountKg ?? 0);
    const size = Number(packingType ?? 0);

    if (total > 0 && size > 0) {
      return Math.round(total / size);
    }

    return undefined;
  };

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  useEffect(() => {
    const run = async () => {
      setProcessing(true);
      try {
        const [wResp, rResp, pResp, poResp]: [
          AxiosResponse,
          AxiosResponse,
          AxiosResponse,
          AxiosResponse,
        ] = await Promise.all([
          getAllWarehouses(),
          getProcessingRequests({}),
          getAllProducts(),
          getProcessingOrders(),
        ]);

        ensureSuccess(wResp);
        ensureSuccess(rResp);
        ensureSuccess(pResp);
        ensureSuccess(poResp);

        setWarehousesApi(wResp?.data?.result ?? []);
        setRequests(rResp?.data?.result ?? []);
        setProducts(pResp?.data?.result ?? []);
        setProcessingOrders(poResp?.data?.result ?? []);
      } catch (e) {
        console.log(e);
      } finally {
        setProcessing(false);
      }
    };
    run();
  }, []);

  const loadOperatorOrder = () => {
    if (!newProcessingOrder.id) return;

    getOperatorOrders(newProcessingOrder.id)
      .then(async (response: AxiosResponse) => {
        const items = (response.data.result ?? []) as OperatorOrderDetails[];

        if (items.length > 0 && items[0].id) {
          const detailsResp = await getOneOperatorOrder(items[0].id);
          const details = detailsResp?.data?.result as OperatorOrderDetails;
          setOperatorOrder(details);
        } else {
          setOperatorOrder(undefined);
        }
      })
      .catch((error: AxiosError) => {
        console.log("Greška pri učitavanju naloga za operatere:", error);
      });
  };

  useEffect(() => {
    if (newProcessingOrder.id) {
      loadOperatorOrder();
    }
  }, [newProcessingOrder.id]);

  useEffect(() => {
    if (id) {
      setProcessing(true);
      setIsPdfReady(false);
      getOneProcessingOrder(id)
        .then((response: AxiosResponse) => {
          const newOrder = response.data.result;
          if (newOrder) {
            if (newOrder.date) {
              newOrder.date = new Date(newOrder.date);
            }
            if (newOrder.approvalDate) {
              newOrder.approvalDate = new Date(newOrder.approvalDate);
            }
            setNewProcessingOrder(newOrder);
            if (newOrder.request?.id) {
              const available = calculateAvailableAmountForRequest(
                newOrder.request.id,
                newOrder.request.amountKg,
              );
              setAvailableAmountKg(available);
            }
            setUpdating(true);
            setIsPdfReady(true);
          }
        })
        .catch((error: AxiosError) => {
          console.log(error);
        })
        .finally(() => {
          setProcessing(false);
        });
    } else {
      setIsPdfReady(true);
      setProcessing(false);
    }
  }, [id]);

  useEffect(() => {
    const generatePdf = () => {
      if (!isPdfReady) return;
      const newPdf = getProcessingOrderTemplate(newProcessingOrder);
      setPdfData(newPdf);
    };

    const timer = setTimeout(generatePdf, 100);
    return () => clearTimeout(timer);
  }, [newProcessingOrder, isPdfReady]);

  useEffect(() => {
    if (!newProcessingOrder.request?.id) {
      setAvailableAmountKg(undefined);
      return;
    }

    const available = calculateAvailableAmountForRequest(
      newProcessingOrder.request.id,
      newProcessingOrder.request.amountKg,
    );

    setAvailableAmountKg(available);
  }, [processingOrders, newProcessingOrder.request?.id, newProcessingOrder.id]);

  const toast = useRef(null);

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({
    mode: "all",
  });

  const onSubmit = () => {
    const currentTotalAmountKg = Number(newProcessingOrder.totalAmountKg ?? 0);
    const currentAvailableAmountKg = Number(availableAmountKg ?? 0);
    const hasPersistedBatches =
      updating && (newProcessingOrder.batches?.some((b) => !!b.id) ?? false);
    if (!currentTotalAmountKg || currentTotalAmountKg <= 0) {
      //@ts-ignore
      toast.current.show({
        severity: "error",
        detail: t(`Ukupna količina mora biti veća od 0.`),
        life: 3000,
      });
      return;
    }

    if (!updating && currentTotalAmountKg > currentAvailableAmountKg) {
      //@ts-ignore
      toast.current.show({
        severity: "error",
        detail: t(`Ukupna količina ne može biti veća od raspoložive količine.`),
        life: 3000,
      });
      return;
    }

    if (
      updating &&
      !hasPersistedBatches &&
      currentTotalAmountKg > currentAvailableAmountKg
    ) {
      //@ts-ignore
      toast.current.show({
        severity: "error",
        detail: t(`Ukupna količina ne može biti veća od raspoložive količine.`),
        life: 3000,
      });
      return;
    }
    const payload = {
      id: newProcessingOrder.id,
      requestId: newProcessingOrder.requestId,
      number: newProcessingOrder.number,
      date: newProcessingOrder.date
        ? Utility.toMiddayDate(new Date(newProcessingOrder.date as Date))
        : new Date(),
      processingCenterId:
        newProcessingOrder.processingCenterId ??
        newProcessingOrder.processingCenter?.id,
      plantType: newProcessingOrder.plantType,
      sortId: newProcessingOrder.sortId ?? newProcessingOrder.sort?.id,
      seedCategory: newProcessingOrder.seedCategory,
      approvalNumber: newProcessingOrder.approvalNumber,
      approvalDate: newProcessingOrder.approvalDate,
      packingType: newProcessingOrder.packingType,
      packageCount: newProcessingOrder.packageCount,
      totalAmountKg: newProcessingOrder.totalAmountKg,
      treatment: newProcessingOrder.treatment,
      atestType: newProcessingOrder.atestType,
      atestOECD: newProcessingOrder.atestOECD,
      atestORANZ: newProcessingOrder.atestORANZ,
      atestGMO: newProcessingOrder.atestGMO,
      atestRwaExport: newProcessingOrder.atestRwaExport,
      barcode: newProcessingOrder.barcode,
      contactPerson: newProcessingOrder.contactPerson,
      batches: newProcessingOrder.batches,
    };
    if (updating) {
      updateOneProcessingOrder(id as string, payload).then(
        (response: AxiosResponse) => {
          if (response.status === 200 || response.status === 201) {
            //@ts-ignore
            toast.current.show({
              severity: "success",
              detail: t(`Uspešno izmenjen nalog za doradu i pakovanje`),
              life: 3000,
            });
            navigate(`/processing-orders`);
          } else {
            //@ts-ignore
            toast.current.show({
              severity: "error",
              detail: t(
                `Nalog za doradu i pakovanje nije snimljen, proverite podatke`,
              ),
              life: 3000,
            });
          }
        },
      );
    } else {
      addNewProcessingOrder(payload).then((response: AxiosResponse) => {
        if (response.status === 200 || response.status === 201) {
          //@ts-ignore
          toast.current.show({
            severity: "success",
            detail: t(`Uspešno dodat nalog za doradu i pakovanje`),
            life: 3000,
          });
          navigate(`/processing-orders`);
        } else {
          //@ts-ignore
          toast.current.show({
            severity: "error",
            detail: t(
              `Nalog za doradu i pakovanje nije dodat, proverite podatke`,
            ),
            life: 3000,
          });
        }
      });
    }
  };

  const onError = () => {
    //@ts-ignore
    toast.current.show({
      severity: "error",
      detail: t(`Neispravno popunjena polja`),
      life: 3000,
    });
  };

  const handleResetForm = () => {
    setAvailableAmountKg(undefined);
    setNewProcessingOrder({} as ProcessingOrderDetails);
  };

  const handleNewProcessingOrder = (prop: string, value: any) => {
    setNewProcessingOrder((newProcessingOrder) => ({
      ...newProcessingOrder,
      [prop]: value,
    }));
  };

  const handleWarehouseChange = (prop: string, value: any) => {
    let copyOfNewProcessingOrder = cloneDeep(newProcessingOrder);
    const foundedWarehouse = warehousesApi.find(
      (item) => item.id === value,
    ) as Warehouse;
    copyOfNewProcessingOrder.processingCenter = foundedWarehouse;
    copyOfNewProcessingOrder.processingCenterId = foundedWarehouse.id;
    setNewProcessingOrder(copyOfNewProcessingOrder);
  };

  const handleSortChange = (prop: string, value: any) => {
    setNewProcessingOrder((prev) => {
      const copy = cloneDeep(prev);
      const selectedProduct = products.find((item) => item.id === value);

      copy.sort = selectedProduct;
      copy.sortId = selectedProduct?.id;

      return copy;
    });
  };

  const generateBatches = (totalAmountKg?: number) => {
    const batches: { code: string; planAmountKg?: number; note?: string }[] =
      [];
    const total = Number(totalAmountKg ?? 0);
    if (!total || total <= 0) return batches;

    const BATCH_SIZE = 20000;
    const fullBatches = Math.floor(total / BATCH_SIZE);
    const remainder = total - fullBatches * BATCH_SIZE;

    for (let i = 0; i < fullBatches; i++) {
      batches.push({
        code: "",
        planAmountKg: BATCH_SIZE,
        note: "",
      });
    }

    if (remainder > 0) {
      batches.push({
        code: "",
        planAmountKg: remainder,
        note: "",
      });
    }

    return batches;
  };

  const handleRequestChange = (prop: string, value: any) => {
    let copyOfNewProcessingOrder = cloneDeep(newProcessingOrder);
    const foundedRequest = requests.find(
      (item) => item.id === value,
    ) as ProcessingRequestDetails;

    copyOfNewProcessingOrder.request = foundedRequest;
    copyOfNewProcessingOrder.requestId = foundedRequest.id;

    const available = calculateAvailableAmountForRequest(
      foundedRequest.id,
      foundedRequest.amountKg,
    );

    copyOfNewProcessingOrder.totalAmountKg = available;

    const approvals = foundedRequest.approvals ?? [];
    copyOfNewProcessingOrder.approvalNumber = approvals
      .filter((a) => !!a.number)
      .map((a) => a.number)
      .join(", ");

    copyOfNewProcessingOrder.approvalDate =
      approvals.length > 0 && approvals[0].date
        ? (new Date(approvals[0].date as any) as any)
        : undefined;

    copyOfNewProcessingOrder.packageCount = recalculatePackageCount(
      copyOfNewProcessingOrder.totalAmountKg,
      copyOfNewProcessingOrder.packingType,
    );

    copyOfNewProcessingOrder.batches = generateBatches(
      copyOfNewProcessingOrder.totalAmountKg as any,
    );

    setAvailableAmountKg(available);
    setNewProcessingOrder(copyOfNewProcessingOrder);
  };

  const handleTotalAmountKgChange = (prop: string, value: any) => {
    setNewProcessingOrder((prev) => {
      const copy = cloneDeep(prev);

      const parsedValue =
        value === "" || value === null || value === undefined
          ? undefined
          : Number(value);

      copy.totalAmountKg =
        parsedValue === undefined || Number.isNaN(parsedValue)
          ? undefined
          : Math.round(parsedValue);

      copy.packageCount = recalculatePackageCount(
        copy.totalAmountKg,
        copy.packingType,
      );

      copy.batches = generateBatches(copy.totalAmountKg);

      return copy;
    });
  };

  const handlePackingTypeChange = (prop: string, value: any) => {
    setNewProcessingOrder((prev) => {
      const copy = cloneDeep(prev);

      copy.packingType = value;
      copy.packageCount = recalculatePackageCount(
        copy.totalAmountKg,
        copy.packingType,
      );

      return copy;
    });
  };

  const handleBatchChange = (
    index: number,
    field: "code" | "note" | "planAmountKg",
    value: any,
  ) => {
    setNewProcessingOrder((prev) => {
      const copy = cloneDeep(prev);
      const batches = [...(copy.batches ?? [])];

      const current = batches[index] ?? {
        code: "",
        note: "",
        planAmountKg: undefined,
      };

      if (field === "code") {
        current.code = value;
      } else if (field === "note") {
        current.note = value;
      } else if (field === "planAmountKg") {
        const num =
          value === "" || value === null || value === undefined
            ? undefined
            : Number(value);
        current.planAmountKg = Number.isNaN(num) ? undefined : num;
      }

      batches[index] = current;
      copy.batches = batches;
      return copy;
    });
  };

  let addButtonSaveStyle: React.CSSProperties = {};
  if (buttonSaveStyle) {
    addButtonSaveStyle = buttonSaveStyle;
  } else {
    addButtonSaveStyle.background = "rgba(0, 165, 83, 1)";
    addButtonSaveStyle.border = "none";
    addButtonSaveStyle.margin = "0 .5rem";
  }

  let addButtonNewPartnerStyle: React.CSSProperties = {};
  if (buttonNewPartnerStyle) {
    addButtonNewPartnerStyle = buttonNewPartnerStyle;
  } else {
    addButtonNewPartnerStyle.background = "rgba(21, 115, 71, 1)";
    addButtonNewPartnerStyle.border = "none";
  }

  let addButtonResetStyle: React.CSSProperties = {};
  if (buttonResetStyle) {
    addButtonResetStyle = buttonResetStyle;
  } else {
    addButtonResetStyle.background = "rgba(220, 53, 69, 1)";
    addButtonResetStyle.border = "none";
  }

  const seedProducts = products.filter(
    (oneProd: Product) => oneProd.category?.name === "SAAT - Seme",
  );

  const hasPersistedBatches =
    updating && (newProcessingOrder.batches?.some((b) => !!b.id) ?? false);

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="form-wrapper">
        <div className="form-header">
          <div className="header-title">
            <i
              className="bi bi-box header-title-icon"
              style={{ fontSize: 18 }}
            ></i>
            <span className="header-title">
              {`${t("Nalog za doradu i pakovanje broj")}: `}
              {newProcessingOrder.number ?? ""}
            </span>
          </div>
          <div className="header-buttons">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={t("Svi nalozi")}
              icon="bi bi-arrow-left-square"
              onButtonClick={() => {
                navigate(`/processing-orders`);
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={""}
              icon="bi bi-file-earmark-pdf"
              onButtonClick={() => {
                if (newProcessingOrder.number) {
                  if (downloadPdfDocument.current) {
                    // @ts-ignore
                    downloadPdfDocument.current();
                  }
                } else {
                  //@ts-ignore
                  toast.current.show({
                    severity: "error",
                    detail: t(`Nije moguće preuzeti nalog koji nije snimljen.`),
                    life: 3000,
                  });
                }
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
              }}
              labelText={""}
              icon="bi bi-printer"
              onButtonClick={() => {
                if (newProcessingOrder.number) {
                  if (printPdfDocument.current) {
                    // @ts-ignore
                    printPdfDocument.current();
                  }
                } else {
                  //@ts-ignore
                  toast.current.show({
                    severity: "error",
                    detail: t(
                      `Nije moguće odštampati nalog koji nije snimljena.`,
                    ),
                    life: 3000,
                  });
                }
              }}
            />
          </div>
        </div>
        {processing ? (
          <ProgressSpinner />
        ) : (
          <div className="form-body">
            <div className="form-input-fields">
              <DropdownInput
                id="processingCenter"
                value={newProcessingOrder.processingCenter?.id}
                onValueChange={handleWarehouseChange}
                optionValue="id"
                optionLabel="name"
                options={warehousesApi.filter(
                  (item) => item.finishingCenter === true,
                )}
                filter={false}
                labelText={`${t("Doradni centar")} *`}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
              />
              <DateInput
                id="date"
                value={
                  newProcessingOrder?.date
                    ? (newProcessingOrder.date as Date)
                    : new Date()
                }
                onValueChange={handleNewProcessingOrder}
                labelText={`${t("Datum")} *`}
                placeholderText={t("Izaberite datum")}
                icon="bi bi-calendar-check"
                maxDate={new Date()}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
              />
              <TextInput
                id="number"
                value={newProcessingOrder.number ?? ""}
                onValueChange={handleNewProcessingOrder}
                labelText={`${t("Broj naloga")}`}
                placeholderText={t("Broj se kreira automatski pri snimanju")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              <DropdownInput
                id="request"
                value={newProcessingOrder.request?.id}
                onValueChange={handleRequestChange}
                optionValue="id"
                optionLabel="number"
                options={requests}
                filter={true}
                labelText={`${t("Prijemni zahtev")} *`}
                placeholderText={t("Izaberite prijemni zahtev")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={id !== undefined}
              />
              <DropdownInput
                id="plantType"
                value={newProcessingOrder.plantType}
                onValueChange={handleNewProcessingOrder}
                options={[
                  { label: "Pšenica", value: "Pšenica" },
                  { label: "Ječam", value: "Ječam" },
                  { label: "Soja", value: "Soja" },
                  { label: "Suncokret", value: "Suncokret" },
                  { label: "Uljana repica", value: "Uljana repica" },
                  { label: "Kukuruz", value: "Kukuruz" },
                ]}
                labelText={`${t("Biljna vrsta")} *`}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
              />
              <DropdownInput
                id="sort"
                value={newProcessingOrder.sort?.id}
                onValueChange={handleSortChange}
                optionValue="id"
                optionLabel="name"
                options={seedProducts}
                filter={true}
                labelText={`${t("Sorta")} *`}
                placeholderText={t("Izaberite sortu")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: false }}
              />
              <TextInput
                id="seedCategory"
                value={newProcessingOrder.seedCategory ?? ""}
                onValueChange={handleNewProcessingOrder}
                labelText={`${t("Kategorija semena")} *`}
                placeholderText={t("Unesite kategoriju semena")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: false }}
              />
              <div className="empty-field"></div>
              {newProcessingOrder.request?.approvals &&
                newProcessingOrder.request.approvals.length > 0 &&
                newProcessingOrder.request.approvals.map(
                  (ap: any, index: number) => (
                    <Fragment key={ap.id ?? index}>
                      <TextInput
                        id={`approvalNumber_${index}`}
                        value={ap.number ?? ""}
                        onValueChange={() => {}}
                        labelText={
                          index === 0
                            ? (t("Broj aprobacionog uverenja") as string)
                            : ""
                        }
                        placeholderText=""
                        control={control}
                        setValue={setValue}
                        errors={errors}
                        disabled={true}
                      />
                      <DateInput
                        id={`approvalDate_${index}`}
                        value={ap.date ? new Date(ap.date as any) : new Date()}
                        onValueChange={() => {}}
                        labelText={
                          index === 0
                            ? (t("Datum aprobacionog uverenja") as string)
                            : ""
                        }
                        placeholderText=""
                        icon="bi bi-calendar-check"
                        control={control}
                        setValue={setValue}
                        errors={errors}
                        disabled={true}
                      />
                    </Fragment>
                  ),
                )}
              <DropdownInput
                id="packingType"
                value={newProcessingOrder.packingType}
                onValueChange={handlePackingTypeChange}
                options={[
                  { label: "25/1", value: "25" },
                  { label: "30/1", value: "30" },
                  { label: "500/1", value: "500" },
                  { label: "600/1", value: "600" },
                  { label: "1000/1", value: "1000" },
                ]}
                labelText={`${t("Vrsta pakovanja")} *`}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={!newProcessingOrder.request}
              />
              <TextInput
                id="packageCount"
                value={
                  newProcessingOrder?.packageCount !== undefined &&
                  newProcessingOrder?.packageCount !== null
                    ? Math.round(newProcessingOrder.packageCount).toString()
                    : ""
                }
                onValueChange={handleNewProcessingOrder}
                labelText={`${t("Broj pakovanja")} *`}
                placeholderText={t("")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              <TextInput
                id="totalAmountKg"
                value={
                  newProcessingOrder?.totalAmountKg !== undefined &&
                  newProcessingOrder?.totalAmountKg !== null
                    ? Math.round(newProcessingOrder.totalAmountKg).toString()
                    : ""
                }
                onValueChange={handleTotalAmountKgChange}
                labelText={`${t("Ukupna količina")} *${
                  newProcessingOrder.request
                    ? ` (${t("max")}: ${Math.round(availableAmountKg ?? 0)} kg)`
                    : ""
                }`}
                placeholderText={t("Unesite ukupnu količinu u kg")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={!newProcessingOrder.request || hasPersistedBatches}
              />
              <TextInput
                id="treatment"
                value={newProcessingOrder?.treatment ?? ""}
                onValueChange={handleNewProcessingOrder}
                labelText={`${t("Tretman")} *`}
                placeholderText={t("Unesite tretman semena")}
                control={control}
                setValue={setValue}
                errors={errors}
              />
              <DropdownInput
                id="atestType"
                value={newProcessingOrder.atestType}
                onValueChange={handleNewProcessingOrder}
                options={[
                  { label: "Domaći", value: AtestType.Domaci },
                  { label: "Strani", value: AtestType.Strani },
                ]}
                labelText={`${t("Atest")} *`}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
              />
              {newProcessingOrder.atestType === AtestType.Domaci ? (
                <TextInput
                  id="barcode"
                  value={newProcessingOrder?.barcode ?? ""}
                  onValueChange={handleNewProcessingOrder}
                  labelText={`${t("Bar kod")} *`}
                  placeholderText={t("Unesite bar kod")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                />
              ) : (
                <div className="empty-field"></div>
              )}
              {newProcessingOrder.atestType === AtestType.Strani && (
                <div className="checkbox-container">
                  <CheckboxInput
                    id="atestOECD"
                    checked={!!newProcessingOrder?.atestOECD}
                    onValueChange={handleNewProcessingOrder}
                    labelText={t("OECD")}
                    control={control}
                    errors={errors}
                  />
                  <CheckboxInput
                    id="atestORANZ"
                    checked={!!newProcessingOrder?.atestORANZ}
                    onValueChange={handleNewProcessingOrder}
                    labelText={t("ORANŽ")}
                    control={control}
                    errors={errors}
                  />
                </div>
              )}
              {newProcessingOrder.atestType === AtestType.Strani && (
                <div className="checkbox-container">
                  <CheckboxInput
                    id="atestGMO"
                    checked={!!newProcessingOrder?.atestGMO}
                    onValueChange={handleNewProcessingOrder}
                    labelText={t("GMO Analiza")}
                    control={control}
                    errors={errors}
                  />
                  <CheckboxInput
                    id="atestRwaExport"
                    checked={!!newProcessingOrder?.atestRwaExport}
                    onValueChange={handleNewProcessingOrder}
                    labelText={t("RWA izvozni")}
                    control={control}
                    errors={errors}
                  />
                </div>
              )}
              {newProcessingOrder.hasDeclarations && (
                <div className="batches-info">
                  <i className="bi bi-info-circle batches-info-icon" />
                  <span>
                    {t(
                      "Za ovaj nalog već postoje deklaracije. Podaci o partijama (broj partije i napomena) ne mogu se menjati.",
                    )}
                  </span>
                </div>
              )}
              {newProcessingOrder.batches &&
                newProcessingOrder.batches.length > 0 && (
                  <div className="batches-grid">
                    {newProcessingOrder.batches.map((b, index) => (
                      <Fragment key={b.id ?? index}>
                        <TextInput
                          id={`batchCode_${index}`}
                          value={b.code ?? ""}
                          onValueChange={(_, v) =>
                            handleBatchChange(index, "code", v)
                          }
                          labelText={
                            index === 0 ? (t("Broj partije") as string) : ""
                          }
                          placeholderText={t("Unesite broj partije")}
                          control={control}
                          setValue={setValue}
                          errors={errors}
                          disabled={!!newProcessingOrder.hasDeclarations}
                        />
                        <TextInput
                          id={`batchPlanAmountKg_${index}`}
                          value={
                            b.planAmountKg !== undefined &&
                            b.planAmountKg !== null
                              ? Math.round(b.planAmountKg).toString()
                              : ""
                          }
                          onValueChange={(_, v) =>
                            handleBatchChange(index, "planAmountKg", v)
                          }
                          labelText={
                            index === 0
                              ? (t("Količina partije (kg)") as string)
                              : ""
                          }
                          placeholderText={t("Unesite količinu u kg")}
                          control={control}
                          setValue={setValue}
                          errors={errors}
                          disabled={!!newProcessingOrder.hasDeclarations}
                        />
                        <TextInput
                          id={`batchNote_${index}`}
                          value={b.note ?? ""}
                          onValueChange={(_, v) =>
                            handleBatchChange(index, "note", v)
                          }
                          labelText={
                            index === 0 ? (t("Napomena") as string) : ""
                          }
                          placeholderText={t("Unesite napomenu")}
                          control={control}
                          setValue={setValue}
                          errors={errors}
                          disabled={!!newProcessingOrder.hasDeclarations}
                        />
                      </Fragment>
                    ))}
                  </div>
                )}
              <TextInput
                id="contactPerson"
                value={newProcessingOrder?.contactPerson ?? ""}
                onValueChange={handleNewProcessingOrder}
                labelText={`${t("Kontakt u doradnom centru")}`}
                placeholderText={t("Unesite kontakt u doradnom centru")}
                control={control}
                setValue={setValue}
                errors={errors}
              />
              <div className="empty-field"></div>
              {newProcessingOrder.id ? (
                <OperatorOrderForm
                  orderId={newProcessingOrder.id}
                  operatorOrder={operatorOrder}
                  parentProcessingOrder={newProcessingOrder}
                  buttonLabel="Nalog za operatere"
                  buttonIcon="bi bi-person-workspace"
                  refreshingData={loadOperatorOrder}
                />
              ) : (
                <div></div>
              )}

              <div className="footer-buttons">
                <ButtonInput
                  labelText={t("Sačuvaj")}
                  onButtonClick={handleSubmit(onSubmit, onError)}
                  addStyle={addButtonSaveStyle}
                />
                <ButtonInput
                  labelText={t("Resetuj")}
                  onButtonClick={handleResetForm}
                  addStyle={addButtonResetStyle}
                />
              </div>
            </div>
            <div className="documents-preview">
              {pdfData ? (
                <PdfPreview
                  pdfDefinition={pdfData}
                  downloadDocument={downloadPdfDocument}
                  printDocument={printPdfDocument}
                  documentName={`${t("Nalog za doradu i pakovanje")}_${
                    newProcessingOrder?.number
                  }.pdf`}
                />
              ) : (
                <p>Učitavam PDF prikaz...</p>
              )}
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};
